import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import InlineLoading from "../components/InlineLoading";
import ReportBranchPicker from "../components/ReportBranchPicker";
import useCurrentUser from "../hooks/useCurrentUser";
import { getSalesSummary, getSalesBreakdown } from "../api";
import { hasPermission } from "../utils/permissions";
import { getDateKey, getRangeFromDays } from "../utils/datePresets";
import { normalizePaginatedResponse } from "../utils/pagination";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString("de-DE")}`;
const formatNumber = (value) => Number(value || 0).toLocaleString("de-DE");

const buildPresets = () => {
  const today = new Date();
  const todayKey = getDateKey(today);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = getDateKey(yesterday);
  const last7 = getRangeFromDays(7);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const thisYearStart = new Date(today.getFullYear(), 0, 1);
  const thisYearEnd = new Date(today.getFullYear(), 11, 31);
  const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

  return [
    {
      id: "today",
      label: "Hoy",
      range: { startDate: todayKey, endDate: todayKey },
    },
    {
      id: "yesterday",
      label: "Ayer",
      range: { startDate: yesterdayKey, endDate: yesterdayKey },
    },
    {
      id: "last-7",
      label: "Últimos 7 días",
      range: last7,
    },
    {
      id: "this-month",
      label: "Este mes",
      range: {
        startDate: getDateKey(thisMonthStart),
        endDate: getDateKey(thisMonthEnd),
      },
    },
    {
      id: "last-month",
      label: "Mes anterior",
      range: {
        startDate: getDateKey(lastMonthStart),
        endDate: getDateKey(lastMonthEnd),
      },
    },
    {
      id: "this-year",
      label: "Este año",
      range: {
        startDate: getDateKey(thisYearStart),
        endDate: getDateKey(thisYearEnd),
      },
    },
    {
      id: "last-year",
      label: "Año anterior",
      range: {
        startDate: getDateKey(lastYearStart),
        endDate: getDateKey(lastYearEnd),
      },
    },
  ];
};

const ReportsSalesPage = () => {
  const { branchId: routeBranchId } = useParams();
  const storedScope = localStorage.getItem("reportBranchScope");
  const defaultBranchScope =
    storedScope || routeBranchId || localStorage.getItem("selectedBranchId") || "";
  const [branchScopeId, setBranchScopeId] = useState(defaultBranchScope);
  const resolvedBranchId = branchScopeId;
  const { user } = useCurrentUser();
  const canViewReport =
    hasPermission(user, "sales.read") &&
    hasPermission(user, "branch_expense.read");
  const canViewByBranch = hasPermission(user, "branch.access.any");
  const isAllBranches = branchScopeId === "all";

  const presets = useMemo(() => buildPresets(), []);
  const defaultRange = presets.find((preset) => preset.id === "today")?.range;

  const [startDate, setStartDate] = useState(defaultRange?.startDate || "");
  const [endDate, setEndDate] = useState(defaultRange?.endDate || "");
  const [activePreset, setActivePreset] = useState("today");

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const [groupBy, setGroupBy] = useState("variety");
  const [breakdown, setBreakdown] = useState({
    data: [],
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
  });
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("totalAmount");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    if (groupBy === "branch" && (!canViewByBranch || !isAllBranches)) {
      setGroupBy("variety");
    }
  }, [groupBy, canViewByBranch, isAllBranches]);

  useEffect(() => {
    if (branchScopeId) {
      localStorage.setItem("reportBranchScope", branchScopeId);
    }
  }, [branchScopeId]);

  useEffect(() => {
    if (!canViewByBranch && branchScopeId === "all") {
      const fallback = localStorage.getItem("selectedBranchId") || "";
      setBranchScopeId(fallback);
    }
  }, [canViewByBranch, branchScopeId]);

  useEffect(() => {
    setPage(1);
  }, [groupBy, startDate, endDate, branchScopeId]);

  const handlePresetClick = (preset) => {
    setActivePreset(preset.id);
    setStartDate(preset.range.startDate);
    setEndDate(preset.range.endDate);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setActivePreset("custom");
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setActivePreset("custom");
  };

  useEffect(() => {
    if (!canViewReport || !resolvedBranchId || !startDate || !endDate) return;
    if (resolvedBranchId === "all" && !canViewByBranch) return;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError("");
      try {
        const data = await getSalesSummary(resolvedBranchId, startDate, endDate);
        setSummary(data);
      } catch (error) {
        console.error("Error fetching sales summary:", error);
        setSummaryError(
          error?.response?.data?.message ||
            "No se pudo cargar el resumen de ventas."
        );
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [canViewReport, resolvedBranchId, startDate, endDate]);

  useEffect(() => {
    if (!canViewReport || !resolvedBranchId || !startDate || !endDate) return;
    if (resolvedBranchId === "all" && !canViewByBranch) return;
    const fetchBreakdown = async () => {
      setBreakdownLoading(true);
      setBreakdownError("");
      try {
        const data = await getSalesBreakdown(
          resolvedBranchId,
          startDate,
          endDate,
          groupBy,
          {
            page,
            pageSize,
            sortBy,
            sortOrder,
          }
        );
        setBreakdown(normalizePaginatedResponse(data));
      } catch (error) {
        console.error("Error fetching sales breakdown:", error);
        setBreakdownError(
          error?.response?.data?.message ||
            "No se pudo cargar el desglose."
        );
      } finally {
        setBreakdownLoading(false);
      }
    };
    fetchBreakdown();
  }, [
    canViewReport,
    resolvedBranchId,
    startDate,
    endDate,
    groupBy,
    page,
    pageSize,
    sortBy,
    sortOrder,
  ]);

  const summaryTotals = summary?.totals || {
    income: 0,
    expenses: 0,
    margin: 0,
    discounts: 0,
    salesCount: 0,
    averageTicket: 0,
  };

  const daily = Array.isArray(summary?.daily) ? summary.daily : [];
  const chartData = useMemo(() => {
    const labels = daily.map((entry) => entry.date);
    return {
      labels,
      datasets: [
        {
          label: "Ingresos",
          data: daily.map((entry) => entry.income),
          borderColor: "#16a34a",
          backgroundColor: "rgba(22, 163, 74, 0.15)",
          tension: 0.3,
        },
        {
          label: "Egresos",
          data: daily.map((entry) => entry.expenses),
          borderColor: "#dc2626",
          backgroundColor: "rgba(220, 38, 38, 0.15)",
          tension: 0.3,
        },
        {
          label: "Margen",
          data: daily.map((entry) => entry.margin),
          borderColor: "#2563eb",
          backgroundColor: "rgba(37, 99, 235, 0.15)",
          tension: 0.3,
        },
      ],
    };
  }, [daily]);

  const breakdownTabs = useMemo(
    () =>
      [
        { id: "variety", label: "Variedades" },
        { id: "client", label: "Clientes" },
        { id: "seller", label: "Vendedores" },
        ...(canViewByBranch && isAllBranches
          ? [{ id: "branch", label: "Sucursales" }]
          : []),
      ],
    [canViewByBranch, isAllBranches]
  );

  const tableConfig = useMemo(() => {
    if (groupBy === "client") {
      return {
        label: "Cliente",
        valueKey: "clientName",
        extraLabel: "Ventas",
        extraKey: "salesCount",
      };
    }
    if (groupBy === "seller") {
      return {
        label: "Vendedor",
        valueKey: "sellerName",
        extraLabel: "Ventas",
        extraKey: "salesCount",
      };
    }
    if (groupBy === "branch") {
      return {
        label: "Sucursal",
        valueKey: "branchName",
        extraLabel: "Ventas",
        extraKey: "salesCount",
      };
    }
    return {
      label: "Variedad",
      valueKey: "varietyName",
      extraLabel: "Cantidad",
      extraKey: "quantity",
    };
  }, [groupBy]);

  if (!canViewReport) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow p-6">
          No tienes permisos para ver este reporte.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reporte de ventas</h1>
          <p className="text-sm text-gray-600">
            KPIs, margen y desglose por periodo.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col gap-4">
            {canViewByBranch && (
              <ReportBranchPicker
                value={branchScopeId}
                onChange={setBranchScopeId}
                className="w-full"
              />
            )}
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    activePreset === preset.id
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:border-blue-300"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => handleStartDateChange(event.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => handleEndDateChange(event.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Evolución diaria</h2>
            </div>
            {summaryLoading ? (
              <div className="py-6">
                <InlineLoading label="Cargando gráfico" />
              </div>
            ) : summaryError ? (
              <div className="text-sm text-red-600">{summaryError}</div>
            ) : daily.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos en el periodo.</div>
            ) : (
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "bottom" },
                    tooltip: {
                      callbacks: {
                        label: (context) =>
                          `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => formatCurrency(value),
                      },
                    },
                  },
                }}
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {[
              { label: "Ingresos", value: summaryTotals.income },
              { label: "Egresos", value: summaryTotals.expenses },
              { label: "Margen", value: summaryTotals.margin },
              { label: "Ventas cerradas", value: summaryTotals.salesCount, isNumber: true },
              { label: "Ticket promedio", value: summaryTotals.averageTicket },
              { label: "Descuentos", value: summaryTotals.discounts },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-lg shadow p-4 flex flex-col gap-1"
              >
                <span className="text-xs text-gray-500 uppercase tracking-wide">
                  {card.label}
                </span>
                {summaryLoading ? (
                  <InlineLoading label="Cargando" />
                ) : (
                  <span className="text-xl font-bold text-gray-900">
                    {card.isNumber
                      ? formatNumber(card.value)
                      : formatCurrency(card.value)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Desglose por {tableConfig.label.toLowerCase()}
            </h2>
            <div className="flex flex-wrap gap-2">
              {breakdownTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setGroupBy(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    groupBy === tab.id
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Ordenar por</label>
              <select
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
              >
                <option value="totalAmount">Monto</option>
                {tableConfig.extraKey && <option value={tableConfig.extraKey}>{tableConfig.extraLabel}</option>}
              </select>
              <select
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              >
                <option value="desc">Descendente</option>
                <option value="asc">Ascendente</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Filas</label>
              <select
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            {breakdownLoading ? (
              <InlineLoading label="Cargando desglose" />
            ) : breakdownError ? (
              <div className="text-sm text-red-600">{breakdownError}</div>
            ) : breakdown.data.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos para este desglose.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3">{tableConfig.label}</th>
                      <th className="py-2 pr-3">{tableConfig.extraLabel}</th>
                      <th className="py-2">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.data.map((row, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 font-medium text-gray-800">
                          {row[tableConfig.valueKey]}
                        </td>
                        <td className="py-2 pr-3 text-gray-600">
                          {formatNumber(row[tableConfig.extraKey] || 0)}
                        </td>
                        <td className="py-2 font-semibold text-gray-900">
                          {formatCurrency(row.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {breakdown.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                Página {breakdown.page} de {breakdown.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={breakdown.page <= 1}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={!breakdown.hasNext}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsSalesPage;
